import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Check, X, FileText, ExternalLink, Mail, Phone, Globe, Linkedin, MapPin, Briefcase } from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { useUser } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/routes/paths';
import {
  useApplication,
  useJobPostings,
  useLinkedOfferLetter,
  useRecruitmentMutations,
} from '../api';
import { ScheduleInterviewModal } from '../components/ScheduleInterviewModal';
import type { ApplicationStage } from '../types';

// ─── Constants (mirrors RecruitmentPage) ─────────────────────────────────────

const STAGE_LABELS: Record<ApplicationStage, string> = {
  applied: 'Applied',
  ai_screened: 'AI Screened',
  contacted: 'Contacted',
  interview: 'Interview',
  hired: 'Hired',
  rejected: 'Rejected',
};

const STAGE_TONES: Record<ApplicationStage, BadgeTone> = {
  applied: 'info',
  ai_screened: 'warning',
  contacted: 'primary',
  interview: 'info',
  hired: 'success',
  rejected: 'danger',
};

const OFFER_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft — needs salary details',
  sent: 'Sent to candidate',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const OFFER_STATUS_TONES: Record<string, BadgeTone> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  rejected: 'danger',
  withdrawn: 'warning',
};

const NEXT_STAGE: Partial<Record<ApplicationStage, ApplicationStage>> = {
  applied: 'ai_screened',
  ai_screened: 'contacted',
  contacted: 'interview',
  interview: 'hired',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/**
 * Full-screen candidate application view, replacing the previous side-drawer.
 * Reached from the Recruitment table (row click / Review action) at
 * /recruitment/applications/:applicationId.
 */
export function ApplicationDetailPage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const user = useUser();

  const { data: app, isLoading, isError } = useApplication(applicationId ?? null);
  const { data: jobs } = useJobPostings();
  const { moveStage, screenApplication } = useRecruitmentMutations();
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);

  const job = jobs?.find((j) => j._id === app?.jobId) ?? null;

  const canManage =
    user?.role === 'superadmin' ||
    user?.role === 'admin' ||
    (user?.department === 'hr' && (user?.role === 'manager' || user?.role === 'team_lead'));

  const hasReachedOffer = !!app && app.stage === 'hired';
  const { data: linkedOffer } = useLinkedOfferLetter(hasReachedOffer ? app?._id ?? null : null);

  function handleAdvance() {
    if (!app) return;
    const next = NEXT_STAGE[app.stage];
    if (!next) return;
    // Moving to 'interview' needs a date/time first — the backend only books
    // a Meeting and drafts the invite email when interviewAt is supplied.
    if (next === 'interview') {
      setShowScheduleInterview(true);
      return;
    }
    moveStage.mutate(
      { id: app._id, stage: next },
      {
        onSuccess: (result) => {
          if (next === 'hired') {
            toast.success(`Moved ${app.candidateName} to Hired — a draft offer letter has been created. Complete it on the Offer Letters page.`, { duration: 6000 });
            if (result?.onboardingLink) toast.success(`Onboarding link: ${result.onboardingLink}`, { duration: 8000 });
          } else {
            toast.success(`Moved ${app.candidateName} to ${STAGE_LABELS[next]}`);
          }
        },
      },
    );
  }

  function confirmScheduleInterview(interviewAt: string, note: string) {
    if (!app) return;
    moveStage.mutate(
      { id: app._id, stage: 'interview', interviewAt, note: note || undefined },
      {
        onSuccess: (result) => {
          toast.success(`Interview scheduled for ${app.candidateName}`);
          setShowScheduleInterview(false);
          if (result?.emailDraft) setEmailDraft(result.emailDraft);
        },
        onError: () => {
          toast.error('Could not schedule the interview.');
        },
      },
    );
  }

  function handleReject() {
    if (!app) return;
    const reason = window.prompt(`Reason for rejecting ${app.candidateName}:`);
    if (reason == null) return; // cancelled
    moveStage.mutate(
      { id: app._id, stage: 'rejected', rejectionReason: reason },
      { onSuccess: () => toast.success(`${app.candidateName} moved to Rejected`) },
    );
  }

  function handleScreen() {
    if (!app) return;
    screenApplication.mutate(app._id, {
      onSuccess: () => toast.success('AI screening initiated'),
    });
  }

  const goBack = () => navigate(ROUTES.recruitment);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={goBack} className="mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to Recruitment
        </Button>
        <EmptyState
          icon={<FileText className="h-6 w-6" />}
          title="Application not found"
          description="This candidate record may have been removed, or you may not have access to it."
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={goBack}
            aria-label="Back to Recruitment"
            className="mt-1 rounded-full p-2 text-on-surface-variant hover:bg-surface-variant"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-on-surface">{app.candidateName}</h1>
              <Badge tone={STAGE_TONES[app.stage]}>{STAGE_LABELS[app.stage]}</Badge>
            </div>
            <p className="mt-1 text-sm text-on-surface-variant">
              {job?.title ?? 'Unknown role'} {job?.location ? `· ${job.location}` : ''} · Applied {timeAgo(app.createdAt)}
            </p>
          </div>
        </div>

        {canManage && app.stage !== 'hired' && app.stage !== 'rejected' && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleAdvance} loading={moveStage.isPending}>
              <Check className="h-4 w-4" /> Advance
            </Button>
            <Button variant="danger" onClick={handleReject} loading={moveStage.isPending}>
              <X className="h-4 w-4" /> Reject
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Main column */}
        <div className="flex flex-col gap-5">
          {/* Contact + application details */}
          <Card>
            <CardHeader title="Candidate Information" />
            <CardBody className="grid gap-3 sm:grid-cols-2">
              <InfoRow icon={Mail} label="Email" value={app.email || '—'} />
              <InfoRow icon={Phone} label="Phone" value={app.phone || '—'} />
              <InfoRow icon={Briefcase} label="Experience" value={app.experience || '—'} />
              <InfoRow icon={MapPin} label="Location" value={app.location || '—'} />
              <InfoRow icon={Briefcase} label="Notice Period" value={app.noticePeriod || '—'} />
              <InfoRow icon={Briefcase} label="Expected CTC" value={app.expectedCTC || '—'} />
              {app.portfolioUrl && (
                <InfoRow icon={Globe} label="Portfolio" value={app.portfolioUrl} href={app.portfolioUrl} />
              )}
              {app.linkedinUrl && (
                <InfoRow icon={Linkedin} label="LinkedIn" value={app.linkedinUrl} href={app.linkedinUrl} />
              )}
              <InfoRow icon={FileText} label="Source" value={app.source} />
              {app.submittedAt && (
                <InfoRow
                  icon={FileText}
                  label="Submitted"
                  value={new Date(app.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                />
              )}
            </CardBody>
          </Card>

          {/* Cover note — the candidate's actual submitted text */}
          {app.coverNote && (
            <Card>
              <CardHeader title="Cover Note" />
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-on-surface">{app.coverNote}</p>
              </CardBody>
            </Card>
          )}

          {/* Resume */}
          <Card>
            <CardHeader title="Resume" />
            <CardBody>
              {app.resumeFile?.storageKey ? (
                <a
                  href={`/api/recruitment/applications/${app._id}/resume`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-3 inline-flex items-center gap-2 rounded-md border border-outline px-3 py-2 text-sm font-medium text-primary hover:bg-surface-variant"
                >
                  <FileText className="h-4 w-4" />
                  {app.resumeFile.originalName || 'View Resume (PDF)'}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <p className="mb-3 text-sm text-on-surface-variant">No résumé file on record for this application.</p>
              )}
              {app.resumeText ? (
                <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md bg-surface-variant p-3 text-xs text-on-surface-variant">
                  {app.resumeText}
                </pre>
              ) : (
                !app.resumeFile?.storageKey && <p className="text-sm text-on-surface-variant">No résumé text extracted.</p>
              )}
            </CardBody>
          </Card>

          {/* Stage history */}
          <Card>
            <CardHeader title="Stage History" />
            <CardBody>
              {app.stageHistory.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No stage transitions yet.</p>
              ) : (
                <ul className="space-y-3">
                  {app.stageHistory.map((event, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      <div>
                        <span className="text-on-surface">
                          {event.fromStage || 'Start'} → {STAGE_LABELS[event.toStage] ?? event.toStage}
                        </span>
                        {event.note && <p className="mt-0.5 text-xs text-on-surface-variant">{event.note}</p>}
                        <p className="mt-0.5 text-xs text-on-surface-variant">{timeAgo(event.occurredAt)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {app.stage === 'rejected' && app.rejectionReason && (
            <Card>
              <CardHeader title="Rejection Reason" />
              <CardBody>
                <p className="text-sm text-on-surface-variant">{app.rejectionReason}</p>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Side column */}
        <div className="flex flex-col gap-5">
          {/* Screening */}
          <Card>
            <CardHeader title="Screening Results" />
            <CardBody>
              {app.screeningScore != null ? (
                <div className="space-y-2">
                  <p className="text-sm text-on-surface-variant">
                    Score: <strong className="text-on-surface">{app.screeningScore}%</strong>
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {(job?.requiredSkills ?? []).map((skill) => {
                      const matched = (app.screeningMatchedSkills ?? []).includes(skill);
                      return (
                        <Badge key={skill} tone={matched ? 'success' : 'danger'} className="text-xs">
                          {matched ? '✓' : '✗'} {skill}
                        </Badge>
                      );
                    })}
                  </div>
                  {app.aiScreeningRemarks && (
                    <p className="mt-3 rounded-md bg-surface-variant p-3 text-sm text-on-surface-variant">
                      {app.aiScreeningRemarks}
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-on-surface-variant">Not screened yet.</p>
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={handleScreen} loading={screenApplication.isPending}>
                      Run AI Screening
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Interview */}
          {app.interviewAt && (
            <Card>
              <CardHeader title="Interview" />
              <CardBody>
                <p className="text-sm text-on-surface-variant">Scheduled: {new Date(app.interviewAt).toLocaleString()}</p>
              </CardBody>
            </Card>
          )}

          {/* Offer letter */}
          {hasReachedOffer && (
            <Card>
              <CardHeader title="Offer Letter" />
              <CardBody>
                {linkedOffer ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-on-surface">{linkedOffer.designation || 'Designation pending'}</p>
                    <Badge tone={OFFER_STATUS_TONES[linkedOffer.status]}>{OFFER_STATUS_LABELS[linkedOffer.status]}</Badge>
                    <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate(ROUTES.offerLetters)}>
                      <FileText className="h-3.5 w-3.5" /> Open in Offer Letters <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-on-surface-variant">Loading offer letter…</p>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleInterview && app && (
        <ScheduleInterviewModal
          open={showScheduleInterview}
          candidateName={app.candidateName}
          loading={moveStage.isPending}
          onClose={() => setShowScheduleInterview(false)}
          onConfirm={confirmScheduleInterview}
        />
      )}

      {/* Interview invite email draft */}
      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-outline/50 bg-surface-variant/30 px-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-on-surface-variant" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase text-on-surface-variant">{label}</p>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="break-all text-sm font-medium text-primary underline">
            {value}
          </a>
        ) : (
          <p className="break-words text-sm font-medium text-on-surface">{value}</p>
        )}
      </div>
    </div>
  );
}
